/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  WeightUnitEnum,
  WeightUnitEnum$inboundSchema,
  WeightUnitEnum$outboundSchema,
} from "./weightunitenum.js";

export type LineItemBase = {
  /**
   * Currency of the <code>total_price</code> amount.
   */
  currency?: string | undefined;
  /**
   * Country the item was manufactured in. In the Shippo dashboard, this value will be used ot pre-fill the customs declaration when creating a label for this order.
   */
  manufactureCountry?: string | undefined;
  /**
   * The date and time this item needs to be delivered by, i.e. by when the carrier delivers it to the buyer.
   *
   * @remarks
   * This value is used by some platforms such as eBay to measure a seller's shipping time and performance.
   * It will be displayed in the Shippo dashboard.
   */
  maxDeliveryTime?: Date | undefined;
  /**
   * The date and time this item needs to be fulfilled by, i.e. by when the shipping label needs to be
   *
   * @remarks
   * created and handed over to the carrier. This value is used by some platforms such as eBay to measure
   * a seller's handling time and performance. It will be displayed in the Shippo dashboard.
   */
  maxShipTime?: Date | undefined;
  /**
   * The quantity of this item in this order.
   */
  quantity?: number | undefined;
  /**
   * The stock keeping unit value of this item.
   */
  sku?: string | undefined;
  /**
   * Title of the line item.
   */
  title?: string | undefined;
  /**
   * Total price paid by the buyer for this item (or these items, if quantity > 1).
   */
  totalPrice?: string | undefined;
  /**
   * A variant is a specific variation of an item (e.g. `size M` or `color blue`).
   *
   * @remarks
   * Variants might be exposed as a separate resource in the future too.
   * Currently the variant title is a free text field describing the variant.
   */
  variantTitle?: string | undefined;
  /**
   * Total weight of this/these item(s). Instead of specifying the weight of all items,
   *
   * @remarks
   * you can also set the <code>total_weight</code> value of the order object.
   */
  weight?: string | undefined;
  /**
   * The unit used for weight.
   */
  weightUnit?: WeightUnitEnum | undefined;
};

/** @internal */
export const LineItemBase$inboundSchema: z.ZodType<
  LineItemBase,
  z.ZodTypeDef,
  unknown
> = z.object({
  currency: z.string().optional(),
  manufacture_country: z.string().optional(),
  max_delivery_time: z.string().datetime({ offset: true }).transform(v =>
    new Date(v)
  ).optional(),
  max_ship_time: z.string().datetime({ offset: true }).transform(v =>
    new Date(v)
  ).optional(),
  quantity: z.number().int().optional(),
  sku: z.string().optional(),
  title: z.string().optional(),
  total_price: z.string().optional(),
  variant_title: z.string().optional(),
  weight: z.string().optional(),
  weight_unit: WeightUnitEnum$inboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    "manufacture_country": "manufactureCountry",
    "max_delivery_time": "maxDeliveryTime",
    "max_ship_time": "maxShipTime",
    "total_price": "totalPrice",
    "variant_title": "variantTitle",
    "weight_unit": "weightUnit",
  });
});

/** @internal */
export type LineItemBase$Outbound = {
  currency?: string | undefined;
  manufacture_country?: string | undefined;
  max_delivery_time?: string | undefined;
  max_ship_time?: string | undefined;
  quantity?: number | undefined;
  sku?: string | undefined;
  title?: string | undefined;
  total_price?: string | undefined;
  variant_title?: string | undefined;
  weight?: string | undefined;
  weight_unit?: string | undefined;
};

/** @internal */
export const LineItemBase$outboundSchema: z.ZodType<
  LineItemBase$Outbound,
  z.ZodTypeDef,
  LineItemBase
> = z.object({
  currency: z.string().optional(),
  manufactureCountry: z.string().optional(),
  maxDeliveryTime: z.date().transform(v => v.toISOString()).optional(),
  maxShipTime: z.date().transform(v => v.toISOString()).optional(),
  quantity: z.number().int().optional(),
  sku: z.string().optional(),
  title: z.string().optional(),
  totalPrice: z.string().optional(),
  variantTitle: z.string().optional(),
  weight: z.string().optional(),
  weightUnit: WeightUnitEnum$outboundSchema.optional(),
}).transform((v) => {
  return remap$(v, {
    manufactureCountry: "manufacture_country",
    maxDeliveryTime: "max_delivery_time",
    maxShipTime: "max_ship_time",
    totalPrice: "total_price",
    variantTitle: "variant_title",
    weightUnit: "weight_unit",
  });
});

/**
 * @internal
 * @deprecated This namespace will be removed in future versions. Use schemas and types that are exported directly from this module.
 */
export namespace LineItemBase$ {
  /** @deprecated use `LineItemBase$inboundSchema` instead. */
  export const inboundSchema = LineItemBase$inboundSchema;
  /** @deprecated use `LineItemBase$outboundSchema` instead. */
  export const outboundSchema = LineItemBase$outboundSchema;
  /** @deprecated use `LineItemBase$Outbound` instead. */
  export type Outbound = LineItemBase$Outbound;
}

export function lineItemBaseToJSON(lineItemBase: LineItemBase): string {
  return JSON.stringify(LineItemBase$outboundSchema.parse(lineItemBase));
}

export function lineItemBaseFromJSON(
  jsonString: string,
): SafeParseResult<LineItemBase, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => LineItemBase$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'LineItemBase' from JSON`,
  );
}
